import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, type Location } from "react-router-dom";
import { Button, FormField, Input, useToast } from "@/ui";
import { ApiError, type CaptchaProof } from "@/api";
import { useAuthStore } from "@/auth";
import { AuthShell } from "@/auth/AuthShell";
import { AuthAlert } from "@/auth/AuthAlert";
import { CaptchaField } from "@/auth/CaptchaField";
import { EyeIcon, EyeOffIcon, LockIcon } from "@/auth/icons";
import { useT } from "./i18n";

/**
 * LoginPage — the unauthenticated entry, wrapped in the premium two-panel AuthShell (the LAB
 * identity chrome). On success it seeds the auth store (token pair + live capability fetch) and
 * routes to the originally requested path (or "/"). Enumeration-safe server errors are surfaced
 * verbatim (already FR, generic) in BOTH an inline error alert (role=alert) and a toast.
 *
 * The auth WIRING is unchanged (D1) — only the chrome + UX around the form is the redesign. The
 * credential fields keep using the UI-kit FormField/Input/Button so every label/aria/focus-ring
 * guarantee is inherited unchanged (and the integration test's label/role queries stay valid).
 */
export function LoginPage() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation() as Location & { state?: { from?: string } };
  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [labRef, setLabRef] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The captcha proof minted by the visible widget — gates the submit button until present.
  const [captcha, setCaptcha] = useState<CaptchaProof | null>(null);
  // The lab-code field is collapsed by default — a single-lab user (the vast majority) never sees it and
  // is never confused by an optional field. It reveals on demand, and STAYS revealed once a login fails
  // (the multi-lab miss is a likely cause) or once a code is already typed. The server stays fully
  // enumeration-safe — a wrong/blank code returns the same generic error (no "which lab" oracle), so this
  // is pure client guidance, never a lab-existence signal.
  const [showLabRef, setShowLabRef] = useState(false);

  const submitting = status === "loading";
  const labRefVisible = showLabRef || !!labRef.trim() || !!error;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!captcha) return;
    setError(null);
    try {
      await login(
        { email, password, ...(labRef.trim() ? { laboratoireRef: labRef.trim() } : {}) },
        captcha,
      );
      navigate(location.state?.from ?? "/", { replace: true });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t("auth.login.error");
      setError(message);
      toast.error(message);
    }
  };

  return (
    <AuthShell>
      <h1 className="nv-auth__h">{t("auth.login.title")}</h1>
      <p className="nv-auth__sub">{t("auth.login.subtitle")}</p>

      {error && <AuthAlert tone="red">{error}</AuthAlert>}
      {/* On a failed login, nudge the multi-lab user toward the (now-revealed) lab-code field. Polite
          (role=status via the cyan tone) so it never steals the assertive error's announcement. */}
      {error && <AuthAlert tone="cyan">{t("auth.login.error.multiLabHint")}</AuthAlert>}

      <form onSubmit={onSubmit} className="nv-auth__form" style={{ marginBlockStart: "var(--nv-s-4)" }}>
        <FormField label={t("auth.login.email")} required>
          {(p) => (
            <Input
              {...p}
              type="email"
              inputMode="email"
              autoComplete="username"
              placeholder={t("auth.login.email.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </FormField>

        <FormField label={t("auth.login.password")} required>
          {(p) => (
            <span className="nv-auth__pw">
              <Input
                {...p}
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("auth.login.password.placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="nv-auth__pw-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? t("auth.login.pw.hide") : t("auth.login.pw.show")}
                aria-pressed={showPw}
              >
                {showPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </span>
          )}
        </FormField>

        {labRefVisible ? (
          <FormField label={t("auth.login.labref")} hint={t("auth.login.labref.hint")}>
            {(p) => (
              <Input
                {...p}
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder={t("auth.login.labref.placeholder")}
                value={labRef}
                onChange={(e) => setLabRef(e.target.value)}
              />
            )}
          </FormField>
        ) : (
          <button
            type="button"
            className="nv-auth__link"
            onClick={() => setShowLabRef(true)}
            style={{
              alignSelf: "flex-start",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {t("auth.login.labref.toggle.show")}
          </button>
        )}

        <div className="nv-auth__row">
          <label className="nv-auth__check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>{t("auth.login.remember")}</span>
          </label>
          <Link to="/mot-de-passe-oublie" className="nv-auth__link">
            {t("auth.login.forgot")}
          </Link>
        </div>

        <CaptchaField onVerified={setCaptcha} onReset={() => setCaptcha(null)} />

        <Button type="submit" block loading={submitting} disabled={!captcha}>
          {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>
      </form>

      <p className="nv-auth__secure">
        <LockIcon />
        <span>{t("auth.login.secure")}</span>
      </p>

      <div className="nv-auth__signup">
        <span>{t("auth.signup.prompt")}</span>
        <Link to="/demande-acces" className="nv-auth__link">
          {t("auth.signup.cta")}
        </Link>
        <span className="nv-auth__signup-note">{t("auth.signup.note")}</span>
      </div>
    </AuthShell>
  );
}
